import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { useFragment } from "react-relay";

import { LessonBookingWizard } from "@/features/BookingWizard";
import { Button } from "@whoosh/design-system/src/components/controls";
import { AnimateHeight } from "@whoosh/design-system/src/components/controls/Accordion/AnimateHeight";
import {
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import { useFacilityLessonPatronAccess } from "../../../../../../../features/Facility/hooks/useFacilityLessonPatronAccess";
import { useUserBelongsToCurrentClub } from "../../../../../../../features/Session";
import { durationAtom, lessonTypeIdAtom } from "../../../atoms";
import { AvailabilitySlotsButton_facility$key } from "./__generated__/AvailabilitySlotsButton_facility.graphql";
import { AvailabilitySlots_session$key } from "./__generated__/AvailabilitySlots_session.graphql";

type Props = {
  sessionRef: AvailabilitySlots_session$key | null;
  facilityId: string;
  instructorId: string;
  openAuthModal: (callback: () => void) => void;
  slots: Array<{
    date: string;
    durationMins: number;
    id: string;
    time: string;
  }>;
  facilityNodeRef: AvailabilitySlotsButton_facility$key | null;
  canSelectVenue: boolean;
};
export const AvailabilitySlots = ({
  slots,
  facilityId,
  instructorId,
  openAuthModal,
  sessionRef,
  facilityNodeRef,
  canSelectVenue,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedLessonTypeId = useAtomValue(lessonTypeIdAtom);
  const selectedDuration = useAtomValue(durationAtom);

  const session = useFragment(AvailabilitySlotsFragmentString, sessionRef);

  const facility = useFragment(
    AvailabilitySlotsButtonFragmentString,
    facilityNodeRef
  );

  const [requestTime, setRequestTime] = useState<string | null>(null);

  const userBelongsToCurrentClub = useUserBelongsToCurrentClub(session ?? null);

  const baseSlotsDisplayCount =
    useResponsiveProp({
      base: 4,
      sm: 6,
      md: 8,
    }) || 4;

  const isExpandable = slots.length > baseSlotsDisplayCount && !isExpanded;

  const slotsToShow = isExpanded
    ? slots
    : slots.slice(0, baseSlotsDisplayCount);

  const patronLessonAccess = useFacilityLessonPatronAccess(facility ?? null);

  const [bookingWizardSlotId, setBookingWizardSlotId] = useState<string | null>(
    null
  );
  const [bookingWizardDate, setBookingWizardDate] = useState<string | null>(
    null
  );
  const handleProcessLessonClick = useCallback(
    (slotId: string, time: string, date: string) => {
      setBookingWizardDate(date);
      setBookingWizardSlotId(slotId);
      if (patronLessonAccess === "REQUEST") {
        setRequestTime(time);
      }
    },
    [patronLessonAccess]
  );
  const onPress = useCallback(
    (slotId: string, time: string, date: string) => {
      if (userBelongsToCurrentClub) {
        handleProcessLessonClick(slotId, time, date);
      } else {
        openAuthModal(() => {
          handleProcessLessonClick(slotId, time, date);
        });
      }
    },
    [handleProcessLessonClick, openAuthModal, userBelongsToCurrentClub]
  );

  return (
    <>
      <AnimateHeight>
        <VStack gap={4}>
          <HStack
            gap={2}
            flexWrap="wrap"
            flex={1}
            justifyContent={{ base: "space-between", sm: "flex-start" }}
          >
            {slotsToShow.map((slot) => {
              const time = slot.time;
              return (
                <Pressable
                  key={slot.id}
                  justifyContent="center"
                  alignContent="center"
                  borderColor="surface-dividers"
                  borderRadius="2"
                  borderWidth={1.5}
                  height={48}
                  backgroundColor="surface-primary"
                  flexBasis={{ base: "48%", sm: "32.1%", md: "23.7%" }}
                  flexGrow={0}
                  onPress={() => {
                    onPress(slot.id, slot.time, slot.date);
                  }}
                  testID={`availability-slot-button-${slot.date}-${slot.time}`}
                  accessibilityLabel={t`Create Booking for ${slot.date} ${time}`}
                >
                  <Text textAlign="center">{formatHHMM(time)}</Text>
                </Pressable>
              );
            })}
          </HStack>
          {isExpandable ? (
            <Button
              variant="outline"
              size="small"
              onPress={() => {
                setIsExpanded(true);
              }}
              accessibilityLabel={t`View All Times button`}
            >{t`View All Times`}</Button>
          ) : null}
        </VStack>
      </AnimateHeight>
      <LessonBookingWizard
        isOpen={!!bookingWizardSlotId}
        onClose={() => {
          setBookingWizardSlotId(null);
          setRequestTime(null);
        }}
        slotId={bookingWizardSlotId}
        membershipTypeId={
          session?.currentClientProfile?.member?.membershipType?.id ?? null
        }
        date={bookingWizardDate}
        currentPatronId={session?.currentClientProfile?.member?.id ?? null}
        clubSlug={session?.currentClientProfile?.member?.club?.slug ?? ""}
        slotType={"MULTI"}
        facilityId={facilityId ?? ""}
        instructorId={instructorId}
        lessonTypeId={selectedLessonTypeId ?? ""}
        canSelectVenue={canSelectVenue}
        slots={slots}
        requestedTime={requestTime}
        durationMins={selectedDuration}
      />
    </>
  );
};

const AvailabilitySlotsFragmentString = graphql`
  fragment AvailabilitySlots_session on Session {
    currentClientProfile {
      ... on MemberProfile {
        id
      }
      member {
        ... on Member {
          id
        }
        ... on Customer {
          id
        }
        club {
          slug
        }
        membershipType {
          id
        }
      }
    }

    ...useUserBelongsToCurrentClub_session
  }
`;

const AvailabilitySlotsButtonFragmentString = graphql`
  fragment AvailabilitySlotsButton_facility on Facility {
    ...useFacilityLessonPatronAccess_facility
  }
`;
